import { extractPalette } from '@/utils/colors';
import api, { API_URL } from '@/services/api';

export type ScoreboardTheme = 'classic' | 'pill' | 'var1' | 'var2' | 'var3' | 'var4';

export type ScoreboardState = {
  homeName: string;
  awayName: string;
  homeLogo?: string;
  awayLogo?: string;
  homeShort?: string;
  awayShort?: string;
  primaryColor?: string; // home color
  secondaryColor?: string; // away color
  homeTextColor?: string; // text color for home label/short
  awayTextColor?: string; // text color for away label/short
  homeScore: number;
  awayScore: number;
  homeFouls?: number;
  awayFouls?: number;
  halfLength: number; // minutes
  theme: ScoreboardTheme;
  externalMatchId?: string;
  active?: boolean;
  timer?: string; // MM:SS
  running?: boolean;
  sidesFlipped?: boolean;
  half?: number;
  qrEvery?: number;
  qrDuration?: number;
};

const DEFAULT_STATE: ScoreboardState = {
  homeName: 'DOMÁCÍ',
  awayName: 'HOSTÉ',
  homeLogo: '',
  awayLogo: '',
  homeShort: 'DOM',
  awayShort: 'HOS',
  primaryColor: '#1e3a8a',
  secondaryColor: '#2563eb',
  homeScore: 0,
  awayScore: 0,
  homeFouls: 0,
  awayFouls: 0,
  halfLength: 45,
  theme: 'pill',
  externalMatchId: '',
  active: false,
  timer: '00:00',
  running: false,
  sidesFlipped: false,
  half: 1,
  qrEvery: 5,
  qrDuration: 60,
};

const STORAGE_KEY = 'scoreboard_state_v1';

export async function getScoreboardState(): Promise<ScoreboardState> {
  // Try backend admin singleton first (if authenticated), then fallback to public, then localStorage
  try {
    const res = await api.get('/admin/scoreboard');
    const fromApi = normalizeFromApi(res.data);
    // mirror to localStorage for fast next-load
    localStorage.setItem(STORAGE_KEY, JSON.stringify(fromApi));
    return { ...DEFAULT_STATE, ...fromApi } as ScoreboardState;
  } catch {
    // Not admin or not logged in; try public
    try {
      const base = (API_URL || '').replace(/\/$/, '');
      const res = await fetch(`${base}/scoreboard`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const fromApi = normalizeFromApi(data);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(fromApi));
        return { ...DEFAULT_STATE, ...fromApi } as ScoreboardState;
      }
    } catch {}
    // Fallback to localStorage
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return DEFAULT_STATE;
      const parsed = JSON.parse(raw);
      return { ...DEFAULT_STATE, ...parsed } as ScoreboardState;
    } catch {
      return DEFAULT_STATE;
    }
  }
}

export async function saveScoreboardState(state: Partial<ScoreboardState>): Promise<ScoreboardState> {
  // Avoid an extra GET on every save: use the last known local snapshot as base
  let base: ScoreboardState = { ...DEFAULT_STATE } as ScoreboardState;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      base = { ...DEFAULT_STATE, ...(parsed || {}) } as ScoreboardState;
    }
  } catch {}
  const next = { ...base, ...state } as ScoreboardState;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {}
  // Attempt to persist to backend if admin
  try {
    await api.put('/admin/scoreboard', toApiPayload(state));
  } catch {
    // ignore if not authorized
  }
  return next;
}

export async function getAdminScoreboard(): Promise<ScoreboardState> {
  const res = await api.get('/admin/scoreboard');
  const fromApi = normalizeFromApi(res.data);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(fromApi));
  return { ...DEFAULT_STATE, ...fromApi } as ScoreboardState;
}

export async function updateAdminScoreboard(patch: Partial<ScoreboardState>): Promise<ScoreboardState> {
  const res = await api.put('/admin/scoreboard', toApiPayload(patch));
  const fromApi = normalizeFromApi(res.data);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(fromApi));
  return { ...DEFAULT_STATE, ...fromApi } as ScoreboardState;
}

export async function getPublicScoreboard(): Promise<ScoreboardState> {
  const base = (API_URL || '').replace(/\/$/, '');
  const res = await fetch(`${base}/scoreboard`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to load public scoreboard');
  const data = await res.json();
  const fromApi = normalizeFromApi(data);
  return { ...DEFAULT_STATE, ...fromApi } as ScoreboardState;
}

// Timer controls (admin)
export async function startTimer(): Promise<void> {
  await api.post('/admin/scoreboard/timer/start');
}
export async function pauseTimer(): Promise<void> {
  await api.post('/admin/scoreboard/timer/pause');
}
export async function resetTimer(): Promise<void> {
  await api.post('/admin/scoreboard/timer/reset');
}
export async function swapSides(): Promise<void> {
  await api.post('/admin/scoreboard/swap-sides');
}
export async function startSecondHalf(): Promise<void> {
  await api.post('/admin/scoreboard/second-half');
}

// Admin: presets
export async function listPresets(): Promise<string[]> {
  const res = await api.get<string[]>('/admin/scoreboard/saves');
  // API returns an array of filenames
  return (res.data || []).slice();
}

export async function savePreset(filename?: string): Promise<{ saved: string }> {
  const payload = filename && filename.trim() ? { filename: filename.trim() } : {};
  const res = await api.post<{ saved: string }>('/admin/scoreboard/save', payload);
  return res.data;
}

export async function loadPreset(filename: string): Promise<void> {
  const name = (filename || '').trim();
  if (!name) throw new Error('Missing filename');
  await api.post('/admin/scoreboard/load', { filename: name });
}

// Admin: sponsors management
export async function listSponsorsAdmin(): Promise<string[]> {
  const res = await api.get<string[]>('/admin/scoreboard/sponsors');
  const list = res.data || [];
  try {
    const base = new URL(API_URL || '', typeof window !== 'undefined' ? window.location.origin : undefined);
    const origin = `${base.protocol}//${base.host}`;
    return list.map((u) => (u && u.startsWith('/uploads/') ? origin + u : u));
  } catch {
    return list;
  }
}

export async function uploadSponsors(files: File[]): Promise<{ saved: number; files?: string[] }> {
  const fd = new FormData();
  for (const f of files) fd.append('files', f);
  const res = await api.post<{ saved: number; files?: string[] }>('/admin/scoreboard/sponsors/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return res.data || { saved: 0 };
}

export async function deleteSponsor(name: string): Promise<void> {
  await api.delete('/admin/scoreboard/sponsors', { params: { name } });
}

export async function prefillSponsorsFromPage(ids?: number[]): Promise<{ saved: number; files?: string[] }> {
  const payload = Array.isArray(ids) && ids.length ? { ids } : {};
  const res = await api.post<{ saved: number; files?: string[] }>('/admin/scoreboard/sponsors/prefill', payload);
  return res.data || { saved: 0 };
}

// Public: sponsors list for overlay
export async function listSponsorsPublic(): Promise<string[]> {
  const res = await api.get<string[]>('/scoreboard/sponsors');
  const list = res.data || [];
  try {
    const base = new URL(API_URL || '', typeof window !== 'undefined' ? window.location.origin : undefined);
    const origin = `${base.protocol}//${base.host}`;
    return list.map((u) => (u && u.startsWith('/uploads/') ? origin + u : u));
  } catch {
    return list;
  }
}

// Admin/public: QR management
export async function getQr(): Promise<string> {
  // Prefer admin endpoint when available to avoid public cache
  try {
    const res = await api.get<{ qr?: string }>('/admin/scoreboard/qr');
    const u = res.data?.qr || '';
    if (u && u.startsWith('/uploads/')) {
      try {
        const base = new URL(API_URL || '', typeof window !== 'undefined' ? window.location.origin : undefined);
        return `${base.protocol}//${base.host}${u}`;
      } catch {}
    }
    return u;
  } catch {
    try {
      const base = (API_URL || '').replace(/\/$/, '');
      const r = await fetch(`${base}/scoreboard/qr`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        const u = data?.qr || '';
        if (u && u.startsWith('/uploads/')) {
          try {
            const b = new URL(API_URL || '', typeof window !== 'undefined' ? window.location.origin : undefined);
            return `${b.protocol}//${b.host}${u}`;
          } catch {}
        }
        return u;
      }
    } catch {}
    return '';
  }
}

export async function uploadQr(file: File): Promise<void> {
  const fd = new FormData();
  fd.append('file', file);
  await api.post('/admin/scoreboard/qr', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
}

// Utilities
export function deriveShort(name?: string): string {
  if (!name) return '---';
  const s = String(name).trim().toUpperCase();
  if (!s) return '---';
  const map: Record<string, string> = {
    'Á':'A','Ä':'A','Å':'A','Â':'A','À':'A',
    'Č':'C','Ć':'C','Ç':'C',
    'Ď':'D',
    'É':'E','Ě':'E','È':'E','Ë':'E','Ê':'E',
    'Í':'I','Ì':'I','Ï':'I','Î':'I',
    'Ň':'N','Ń':'N',
    'Ó':'O','Ö':'O','Ô':'O','Ò':'O',
    'Ř':'R',
    'Š':'S','Ś':'S',
    'Ť':'T',
    'Ú':'U','Ů':'U','Ù':'U','Ü':'U','Û':'U',
    'Ý':'Y',
    'Ž':'Z',
  };
  let out = '';
  for (const ch of s) {
    let c = map[ch] || ch;
    if (c >= 'A' && c <= 'Z') {
      out += c;
      if (out.length === 3) break;
    }
  }
  while (out.length < 3) out += '-';
  return out;
}

export async function derivePrimaryFromLogo(logoUrl?: string): Promise<string | null> {
  if (!logoUrl) return null;
  try {
    const colors = await extractPalette(logoUrl, 5);
    return colors?.[0] || null;
  } catch {
    return null;
  }
}

// Helpers to map API payloads
function normalizeFromApi(d: any): Partial<ScoreboardState> {
  if (!d) return {};
  const absolutize = (u?: string) => {
    try {
      if (!u) return '';
      const s = String(u);
      if (s.startsWith('/uploads/') || s.startsWith('/dist/')) {
        const base = new URL(API_URL || '', typeof window !== 'undefined' ? window.location.origin : undefined);
        return `${base.protocol}//${base.host}${s}`;
      }
      return s;
    } catch {
      return u || '';
    }
  };
  const rawHome = d.homeLogo || d.home_logo || d.home_logo_url || d.HomeLogoURL || '';
  const rawAway = d.awayLogo || d.away_logo || d.away_logo_url || d.AwayLogoURL || '';
  return {
    homeName: d.homeName || d.home_name || d.HomeName || '',
    awayName: d.awayName || d.away_name || d.AwayName || '',
    homeLogo: absolutize(rawHome),
    awayLogo: absolutize(rawAway),
    homeShort: d.homeShort || d.home_short || d.HomeShort || '',
    awayShort: d.awayShort || d.away_short || d.AwayShort || '',
    primaryColor: d.primaryColor || d.primary_color || d.PrimaryColor || undefined,
    secondaryColor: d.secondaryColor || d.secondary_color || d.SecondaryColor || undefined,
    homeTextColor: d.homeTextColor || d.home_text_color || d.HomeTextColor || undefined,
    awayTextColor: d.awayTextColor || d.away_text_color || d.AwayTextColor || undefined,
    homeScore: typeof d.homeScore === 'number' ? d.homeScore : (typeof d.home_score === 'number' ? d.home_score : 0),
    awayScore: typeof d.awayScore === 'number' ? d.awayScore : (typeof d.away_score === 'number' ? d.away_score : 0),
    homeFouls: typeof d.homeFouls === 'number' ? d.homeFouls : (typeof d.home_fouls === 'number' ? d.home_fouls : 0),
    awayFouls: typeof d.awayFouls === 'number' ? d.awayFouls : (typeof d.away_fouls === 'number' ? d.away_fouls : 0),
    halfLength: typeof d.halfLength === 'number' ? d.halfLength : (typeof d.half_length === 'number' ? d.half_length : 45),
    theme: (d.theme || 'pill') as any,
    externalMatchId: d.externalMatchId || d.external_match_id || d.ExternalMatchID || '',
    active: typeof d.active === 'boolean' ? d.active : undefined,
    timer: d.timer || d.Timer || '00:00',
    running: typeof d.running === 'boolean' ? d.running : undefined,
    sidesFlipped: typeof d.sidesFlipped === 'boolean' ? d.sidesFlipped : (typeof d.sides_flipped === 'boolean' ? d.sides_flipped : undefined),
    half: typeof d.half === 'number' ? d.half : undefined,
    qrEvery: typeof d.qrEvery === 'number' ? d.qrEvery : (typeof d.qr_show_every_minutes === 'number' ? d.qr_show_every_minutes : undefined),
    qrDuration: typeof d.qrDuration === 'number' ? d.qrDuration : (typeof d.qr_show_duration_seconds === 'number' ? d.qr_show_duration_seconds : undefined),
  };
}

function toApiPayload(p: Partial<ScoreboardState>) {
  const out: any = {};
  if (p.homeName !== undefined) out.homeName = p.homeName;
  if (p.awayName !== undefined) out.awayName = p.awayName;
  if (p.homeLogo !== undefined) out.homeLogo = p.homeLogo;
  if (p.awayLogo !== undefined) out.awayLogo = p.awayLogo;
  if (p.homeShort !== undefined) out.homeShort = p.homeShort;
  if (p.awayShort !== undefined) out.awayShort = p.awayShort;
  if (p.primaryColor !== undefined) out.primaryColor = p.primaryColor;
  if (p.secondaryColor !== undefined) out.secondaryColor = p.secondaryColor;
  if (p.homeTextColor !== undefined) out.homeTextColor = p.homeTextColor;
  if (p.awayTextColor !== undefined) out.awayTextColor = p.awayTextColor;
  if (p.homeScore !== undefined) out.homeScore = p.homeScore;
  if (p.awayScore !== undefined) out.awayScore = p.awayScore;
  if (p.homeFouls !== undefined) out.homeFouls = p.homeFouls;
  if (p.awayFouls !== undefined) out.awayFouls = p.awayFouls;
  if (p.halfLength !== undefined) out.halfLength = p.halfLength;
  if (p.theme !== undefined) out.theme = p.theme;
  if (p.externalMatchId !== undefined) out.externalMatchId = p.externalMatchId;
  if (p.active !== undefined) out.active = p.active;
  if (p.timer !== undefined) out.timer = p.timer;
  if (p.sidesFlipped !== undefined) out.sidesFlipped = p.sidesFlipped;
  if (p.half !== undefined) out.half = p.half;
  if (p.qrEvery !== undefined) out.qrEvery = p.qrEvery;
  if (p.qrDuration !== undefined) out.qrDuration = p.qrDuration;
  return out;
}

export async function deleteQr(): Promise<void> {
  await api.delete('/admin/scoreboard/qr');
}
